import React from 'react';
import {
  PlainPromoSection,
  PromoSectionBaseProps,
  PromoSectionWithImage,
  PromoSectionWithoutImage,
  PromotionImage,
  PromotionVideo,
} from '@sato/components';

import { Link } from '@components/common/Link';

import {
  IImage,
  IPromotionSection,
  isContentType,
  IVideo,
  ParsedEntry,
} from '../types';

const parseContentfulMediaToPromoSectionMedia = (
  mediaField: ParsedEntry<IImage> | ParsedEntry<IVideo> | undefined
): PromotionImage | PromotionVideo | undefined => {
  if (isContentType(mediaField, 'image')) {
    const { image, aspectRatio: _, ...imageProps } = mediaField;
    const { src, ...assetRest } = image ?? {};

    return {
      ...assetRest,
      ...imageProps, // override asset data with entry data
      src,
      srcType: 'image',
      loading: 'lazy',
      source: 'contentful',
    } satisfies PromotionImage;
  } else if (isContentType(mediaField, 'video')) {
    const { src: videoSrc, variant, poster, ...videoProps } = mediaField;
    const { src, srcType: _srcType, ...assetRest } = videoSrc;

    return {
      src,
      srcType: 'video',
      ...assetRest,
      ...videoProps, // override asset data with entry data
      autoPlay: variant === 'autoplay',
      playOnHover: variant === 'semiautoplay',
      controls: variant === 'controls',
      poster: poster?.src,
    } satisfies PromotionVideo;
  }

  return undefined;
};

export const PromotionSection = ({
  contentType: _contentType,
  internalName: _internalName,
  media,
  ...promotionSection
}: ParsedEntry<IPromotionSection>) => {
  const parsedMedia = parseContentfulMediaToPromoSectionMedia(media);

  const commonProps = {
    heading: promotionSection.heading,
    body: promotionSection.body,
    id: promotionSection.id,
    contentAlign: promotionSection.contentAlign,
    linkComponent: Link,
    ...(promotionSection.href && {
      buttons: [
        {
          children: promotionSection.cta,
          to: promotionSection.href,
        },
      ],
    }),
  } satisfies PromoSectionBaseProps<typeof Link>;

  if (promotionSection.variant === 'simple') {
    return <PlainPromoSection {...commonProps} media={parsedMedia} />;
  }

  if (!parsedMedia) {
    return (
      <PromoSectionWithoutImage
        {...commonProps}
        color={promotionSection.color}
      />
    );
  }

  return (
    <PromoSectionWithImage
      {...commonProps}
      media={parsedMedia}
      color={promotionSection.color}
    />
  );
};
